import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminManualPaymentInfo = () => {
  const [loading, setLoading] = useState(true);
  const [manualPayments, setManualPayments] = useState([]);
  const [error, setError] = useState(null);
  const [totalPayments, setTotalPayments] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const navigate = useNavigate();

  // Load manual payment information data
  const loadManualPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminApiService.getAllManualPayments();
      
      if (response.success) {
        setManualPayments(response.data.manualPayments || []);
        setTotalPayments(response.data.totalPayments || 0);
      } else {
        setError(response.message || 'Failed to load manual payment information');
      }
    } catch (err) {
      console.error('Error loading manual payment information:', err);
      setError('Failed to load manual payment information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle manual payment click to view details
  const handleManualPaymentClick = (ticketId) => {
    navigate(`/admin/dashboard/manual-payment-info/${ticketId}`);
  };

  // Handle status change
  const handleStatusChange = (ticketId, newStatus) => {
    setManualPayments(prevPayments =>
      prevPayments.map(payment =>
        payment.id === ticketId ? { ...payment, status: newStatus } : payment
      )
    );
  };

  // Handle status update submission
  const handleStatusUpdate = async (payment) => {
    const ticketId = payment.id;
    
    try {
      setUpdatingStatus(prev => ({ ...prev, [ticketId]: true }));
      
      const response = await adminApiService.updateTransactionStatus(
        payment.agentId,
        payment.transactionId,
        payment.amount,
        payment.status
      );
      
      if (response.success) {
        toast.success('Transaction status updated successfully!');
        // Reload data to get fresh status
        await loadManualPayments();
      } else {
        toast.error(response.message || 'Failed to update transaction status');
      }
    } catch (err) {
      console.error('Error updating transaction status:', err);
      toast.error('Failed to update transaction status. Please try again.');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(amount);
    } catch (error) {
      return `₹${amount}`;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'VERIFIED':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
      case 'PENDING':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
      case 'REJECTED':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    loadManualPayments();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Manual Payment Information</h1>
          <p className="text-gray-600">View and manage manual payment transactions</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <i className="fas fa-exclamation-circle text-red-400"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Payment Information</h2>
                <p className="text-sm text-gray-600">Total: {totalPayments} manual payment records</p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ticket ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bank Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount (Rs.)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction Slip
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Verified Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {manualPayments.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <i className="fas fa-file-invoice-dollar text-gray-400 text-3xl mb-3"></i>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">No manual payments found</h3>
                        <p className="text-gray-500">No manual payment records are available at the moment.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  manualPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleManualPaymentClick(payment.id)}
                          className="text-red-600 hover:text-red-900 font-medium"
                        >
                          {payment.id || '-'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.bankName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.branch || payment.bankName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.transactionDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.transactionId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button className="text-blue-600 hover:text-blue-900">
                          View Slip
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={payment.status || 'Pending'}
                          onChange={(e) => handleStatusChange(payment.id, e.target.value)}
                          disabled={payment.status?.toUpperCase() === 'VERIFIED'}
                          className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Verified">Verified</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleStatusUpdate(payment)}
                          disabled={updatingStatus[payment.id]}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updatingStatus[payment.id] ? (
                            <div className="flex items-center">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                              Updating...
                            </div>
                          ) : (
                            'Submit'
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminManualPaymentInfo;